import './style.css';
import type { ServerMsg } from '@billycheckers/shared';
import { Net } from './net.js';
import { LobbyScreen } from './screens/lobby.js';
import { GameScreen } from './screens/game.js';
import { THEMES, applyTheme, currentTheme, initTheme } from './theme.js';
import { click, setSound, soundEnabled } from './sound.js';
import { initPwa } from './pwa.js';

initTheme();
initPwa((apply) => {
  // ненавязчивый тост «Доступно обновление» (SPEC §8.5)
  const t = document.createElement('div');
  t.className = 'toast';
  const label = document.createElement('span');
  label.textContent = 'Доступно обновление';
  const btn = document.createElement('button');
  btn.className = 'btn';
  btn.textContent = 'обновить';
  btn.addEventListener('click', apply);
  t.append(label, btn);
  document.body.appendChild(t);
});

const app = document.getElementById('app')!;
const net = new Net();

let roomId: string | null = null;
/** Комнаты, которые пользователь явно покинул: их запоздавшие room:state игнорируются. */
const leftRooms = new Set<string>();

// ── каркас страницы ─────────────────────────────────────────────────────────

const header = document.createElement('p');
header.className = 'eyebrow';
header.textContent = 'BillyCheckers · русские шашки';

const screenHost = document.createElement('div');
screenHost.style.display = 'contents';

const footer = document.createElement('div');
footer.className = 'bar';
for (const t of THEMES) {
  const b = document.createElement('button');
  b.className = 'sw';
  b.title = t.title;
  b.setAttribute('aria-pressed', String(t.id === currentTheme()));
  const s = document.createElement('span');
  s.style.background = t.swatch;
  b.appendChild(s);
  b.addEventListener('click', () => {
    applyTheme(t.id);
    for (const x of footer.querySelectorAll('.sw')) x.setAttribute('aria-pressed', 'false');
    b.setAttribute('aria-pressed', 'true');
    click(0.16);
  });
  footer.appendChild(b);
}
const sndBtn = document.createElement('button');
sndBtn.className = 'btn';
sndBtn.textContent = soundEnabled() ? 'звук' : 'тихо';
sndBtn.addEventListener('click', () => {
  setSound(!soundEnabled());
  sndBtn.textContent = soundEnabled() ? 'звук' : 'тихо';
  if (soundEnabled()) click(0.2);
});
footer.appendChild(sndBtn);

app.append(header, screenHost, footer);

// ── экраны ──────────────────────────────────────────────────────────────────

const lobby = new LobbyScreen({
  onName(name) {
    if (name && name !== net.name) {
      net.name = name;
      net.send({ t: 'hello', name, ...(net.token ? { token: net.token } : {}) });
    }
  },
  onCreate(timeControl, color) {
    net.send({ t: 'room:create', name: '', timeControl, color });
  },
  onJoin(id) {
    leftRooms.delete(id);
    net.send({ t: 'room:join', roomId: id });
  },
});

const game = new GameScreen({
  onLeave() {
    if (roomId) {
      leftRooms.add(roomId);
      net.send({ t: 'room:leave', roomId });
    }
    roomId = null;
    location.hash = '';
    game.hideOverlay();
    showLobby();
  },
  onRematch(accept) {
    if (!roomId) return;
    net.send({ t: accept ? 'rematch:accept' : 'rematch:offer', roomId });
  },
  onMove(move) {
    if (roomId) net.send({ t: 'move', roomId, move });
  },
  onResign() {
    if (roomId) net.send({ t: 'resign', roomId });
  },
  onDraw(action) {
    if (roomId) net.send({ t: `draw:${action}`, roomId });
  },
  onChat(text) {
    if (roomId) net.send({ t: 'chat', roomId, text });
  },
});

function showLobby(): void {
  screenHost.replaceChildren(lobby.el);
  lobby.setName(net.name);
  net.send({ t: 'lobby:subscribe' });
}

function showGame(): void {
  if (screenHost.firstChild !== game.el) screenHost.replaceChildren(game.el);
}

// ── тост соединения ─────────────────────────────────────────────────────────

let toastEl: HTMLElement | null = null;
function toast(text: string | null): void {
  if (toastEl) {
    toastEl.remove();
    toastEl = null;
  }
  if (text) {
    toastEl = document.createElement('div');
    toastEl.className = 'toast';
    toastEl.textContent = text;
    document.body.appendChild(toastEl);
  }
}

let errTimer: number | null = null;
function flashError(text: string): void {
  toast(text);
  if (errTimer !== null) window.clearTimeout(errTimer);
  errTimer = window.setTimeout(() => {
    // не стирать тост «нет соединения», если мы всё ещё оффлайн
    toast(netStatus === 'offline' ? 'нет соединения — переподключаюсь…' : null);
  }, 2500);
}

// ── сеть ────────────────────────────────────────────────────────────────────

function hashRoom(): string | null {
  const m = /^#r\/([a-z0-9]+)$/i.exec(location.hash);
  return m ? m[1]! : null;
}

let netStatus: 'connecting' | 'online' | 'offline' = 'connecting';
net.onStatus = (s) => {
  netStatus = s;
  if (s === 'online') toast(null);
  else if (s === 'offline') toast('нет соединения — переподключаюсь…');
};

net.onReady = () => {
  const target = hashRoom();
  if (target) {
    leftRooms.delete(target);
    roomId = target;
    net.send({ t: 'room:join', roomId: target });
  } else {
    showLobby();
  }
};

net.onMessage = (msg: ServerMsg) => {
  switch (msg.t) {
    case 'lobby:state':
      lobby.update(msg.rooms);
      break;
    case 'room:state': {
      // запоздавшее состояние покинутой комнаты не должно вернуть нас в неё
      if (leftRooms.has(msg.state.roomId)) break;
      roomId = msg.state.roomId;
      if (hashRoom() !== roomId) location.hash = `r/${roomId}`;
      showGame();
      game.update(msg.state);
      break;
    }
    case 'move:applied':
      if (msg.roomId === roomId) {
        const r = game.applyMoveMsg(msg.seq, msg.move, msg.position, msg.clocks);
        if (r === 'applied') game.appendHistory(msg.historyEntry);
        else if (r === 'gap') net.send({ t: 'room:join', roomId }); // пересинхронизация
      }
      break;
    case 'chat':
      if (msg.roomId === roomId) game.addChat(msg.from, msg.text, msg.ts);
      break;
    case 'clock':
      if (msg.roomId === roomId) game.setClocks(msg.white, msg.black);
      break;
    case 'game:over':
      if (msg.roomId === roomId) game.showGameOver({ result: msg.result, reason: msg.reason });
      break;
    case 'error':
      flashError(msg.message);
      // сервер отверг действие — вернуть доску к подтверждённому состоянию
      if (roomId) game.refreshBoard();
      break;
    default:
      break;
  }
};

window.addEventListener('hashchange', () => {
  const target = hashRoom();
  if (target && target !== roomId) {
    leftRooms.delete(target);
    roomId = target;
    net.send({ t: 'room:join', roomId: target });
  } else if (!target && roomId) {
    leftRooms.add(roomId);
    net.send({ t: 'room:leave', roomId });
    roomId = null;
    showLobby();
  }
});

net.connect();
